<script>
  import { createEventDispatcher } from 'svelte';

  import { getCartCount, getAccessibilityRedesignRenderState } from '../../../../../js/stores';
  import { blurOnClick } from '../../../../../js/blurOnClick';
  import { onTouchOrClick } from '../../../../../js/onTouchOrClick';

  import Icon from '../Icon/Icon.svelte';
  import Count from '../Count/Count.svelte';

  const dispatch = createEventDispatcher();

  const countStore = getCartCount();
  const accessibilityRedesignRenderStore = getAccessibilityRedesignRenderState();

  export let isOpen = false;
  export let title = 'Your cart';
  export let url = '';

  function toggle() {
    dispatch('toggle');
  }

  // Goal: make mouse click on desktop go to cart url
  function onClick(e) {
    // If this handler is fired, it was not a touch because the
    // onTouchOrClick action below would have cancelled the mouse events.

    // Furthermore, we can tell if the event was actually a mouse
    // click (i.e. not a keyboard click) by looking at e.detail:
    // https://developer.mozilla.org/en-US/docs/Web/API/UIEvent/detail

    // So, if url was passed and this was a mouse click...
    if (url && e.detail !== 0) {
      // ...act like a link and manually go to url
      window.location = url;
    }
  }
</script>

<button
  type="button"
  class="Toggle nav-button"
  class:isOpen
  class:focus-highlight={!$accessibilityRedesignRenderStore}
  class:toggle-accessibility-redesign={$accessibilityRedesignRenderStore}
  aria-haspopup="true"
  aria-expanded={isOpen}
  on:click={onClick}
  use:onTouchOrClick={toggle}
  use:blurOnClick
>
  <Icon isEmpty={$countStore === 0} />
  {#if $countStore > 0}
    <Count count={$countStore} />
  {/if}
  <span class="title">{title}</span>
</button>

<style>@charset "UTF-8";
.Toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: var(--font-weight-medium, 500);
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 10px;
  transition: none;
}
.Toggle.nav-button:focus {
  outline: none;
}
.Toggle.nav-button.focus-highlight {
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.Toggle.nav-button.focus-highlight::-moz-focus-inner {
  border: 0;
}
.Toggle.nav-button.focus-highlight:focus {
  outline: none;
}
.Toggle.nav-button.focus-highlight::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.Toggle.nav-button.focus-highlight:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.Toggle.nav-button.focus-highlight.isOpen::before {
  opacity: 1;
  transform: scale(1);
}
.Toggle.nav-button.focus-highlight > * {
  position: relative;
}
.Toggle.nav-button.toggle-accessibility-redesign {
  background-image: linear-gradient(var(--color-text), var(--color-text));
  background-size: 0% 2px;
  background-repeat: no-repeat;
  background-position: calc(100% - 2px) 100%;
  transition: background-size 400ms cubic-bezier(0.4, 0, 0.2, 1);
  padding: 20px 10px 20px;
}
.Toggle.nav-button.toggle-accessibility-redesign:focus-visible:not(:active) {
  outline-offset: 2px;
  outline: 2px auto Highlight;
  outline: 2px auto -webkit-focus-ring-color;
}
.Toggle.nav-button.toggle-accessibility-redesign.isOpen {
  background-position: calc(0% + 2px) 100%;
  background-size: calc(100% - 4px) 2px;
}

.title {
  position: absolute;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px, 1px);
  white-space: nowrap;
}</style>
